import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Building2, Network, Fingerprint, Briefcase, Clock, CalendarDays, Building, Calculator } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import Branches from "./Branches";
import Departments from "./Departments";
import Devices from "./Devices";
import Occupations from "./Occupations";
import Schedules from "./Schedules";
import Holidays from "./Holidays";
import Companies from "./Companies";
import PayrollConfig from "./PayrollConfig";

const tabs = [
  { value: "empresas", label: "Empresas", icon: Building },
  { value: "sucursales", label: "Sucursales", icon: Building2 },
  { value: "departamentos", label: "Departamentos", icon: Network },
  { value: "dispositivos", label: "Dispositivos", icon: Fingerprint },
  { value: "ocupaciones", label: "Ocupaciones", icon: Briefcase },
  { value: "horarios", label: "Horarios", icon: Clock },
  { value: "feriados", label: "Feriados", icon: CalendarDays },
  { value: "nomina", label: "Nómina", icon: Calculator },
];

const VALID_TABS = tabs.map((t) => t.value);

export default function SettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = searchParams.get("tab");
  const [active, setActive] = useState(
    initial && VALID_TABS.includes(initial) ? initial : "empresas"
  );

  // Keep query string in sync when user changes tab
  useEffect(() => {
    const queryTab = searchParams.get("tab");
    if (queryTab !== active) {
      const next = new URLSearchParams(searchParams);
      next.set("tab", active);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // React to back/forward navigation that changes ?tab=
  useEffect(() => {
    const queryTab = searchParams.get("tab");
    if (queryTab && VALID_TABS.includes(queryTab) && queryTab !== active) {
      setActive(queryTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return (
    <div data-testid="settings-page">
      <div className="mb-6">
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-zinc-950">
          Configuracion
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          Empresas, sucursales, departamentos, dispositivos, ocupaciones y horarios
        </p>
      </div>

      <Tabs value={active} onValueChange={setActive} className="w-full">
        <TabsList className="border-b border-zinc-200 bg-transparent w-full justify-start rounded-none p-0 h-auto gap-0 mb-6">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-zinc-900 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5 text-sm"
                data-testid={`settings-tab-${tab.value}`}
              >
                <Icon className="w-4 h-4 mr-2" strokeWidth={1.5} />
                {tab.label}
              </TabsTrigger>
            );
          })}
        </TabsList>

        <TabsContent value="empresas">
          <Companies embedded />
        </TabsContent>
        <TabsContent value="sucursales">
          <Branches embedded />
        </TabsContent>
        <TabsContent value="departamentos">
          <Departments embedded />
        </TabsContent>
        <TabsContent value="dispositivos">
          <Devices embedded />
        </TabsContent>
        <TabsContent value="ocupaciones">
          <Occupations embedded />
        </TabsContent>
        <TabsContent value="horarios">
          <Schedules embedded />
        </TabsContent>
        <TabsContent value="feriados">
          <Holidays embedded />
        </TabsContent>
        <TabsContent value="nomina">
          <PayrollConfig embedded />
        </TabsContent>
      </Tabs>
    </div>
  );
}
